import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiUsers, FiSettings, FiChevronUp, FiChevronDown, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { teamsApi, membersApi, fieldOptionsApi, customObjectsApi } from '../services/api';
import { formatDate } from '../utils/dateFormat';

const STANDARD_OBJECT_TYPE_OPTIONS = [
  { value: 'contact', label: 'Contact' },
  { value: 'company', label: 'Company' },
  { value: 'ticket', label: 'Ticket' },
  { value: 'deal', label: 'Deal' },
  { value: 'lead', label: 'Lead' },
];

export default function TeamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [owners, setOwners] = useState([]);
  const [customObjectOptions, setCustomObjectOptions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [editForm, setEditForm] = useState({
    custom_team_name: '',
    member_routing_rule: 1,
    object_type: 'contact',
    is_default: false,
    team_order: 1,
    team_active: true,
    team_weight: null,
    fallback_behavior: 'skip',
    fallback_owner_id: '',
    owner_property_name: '',
    availability_source: 'distributely',
    mock: false,
    live: false
  });

  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('last_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const itemsPerPage = 25;

  const [editingWeightMemberId, setEditingWeightMemberId] = useState(null);
  const [weightDraft, setWeightDraft] = useState('');
  const [savingWeightMemberId, setSavingWeightMemberId] = useState(null);
  const [savingActiveMemberId, setSavingActiveMemberId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importingMembers, setImportingMembers] = useState(false);

  const saveMemberWeight = async (memberId) => {
    const cleaned = weightDraft === '' ? 1 : parseInt(weightDraft, 10);
  
    if (isNaN(cleaned) || cleaned < 1) {
      alert("Weight must be a number >= 1");
      return;
    }
  
    try {
      setSavingWeightMemberId(memberId);
  
      await membersApi.updateMember(memberId, { weight: cleaned });
  
      // Optimistically update local state (faster than refetch)
      setTeamData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.map(m =>
            m.id === memberId ? { ...m, weight: cleaned } : m
          )
        };
      });
  
      // exit edit mode
      setEditingWeightMemberId(null);
      setWeightDraft('');
    } catch (err) {
      console.error("Failed to update weight:", err);
      alert("Failed to update weight. Please try again.");
    } finally {
      setSavingWeightMemberId(null);
    }
  };

  const toggleMemberActive = async (memberId, currentValue) => {
    const nextValue = !currentValue;

    try {
      setSavingActiveMemberId(memberId);

      await membersApi.updateMember(memberId, { member_active: nextValue ? 1 : 0 });

      setTeamData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          members: prev.members.map(m =>
            m.id === memberId ? { ...m, member_active: nextValue } : m
          )
        };
      });
    } catch (err) {
      console.error('Failed to update member status:', err);
      alert('Failed to update member status. Please try again.');
    } finally {
      setSavingActiveMemberId(null);
    }
  };
  


  const fetchTeamData = useCallback(async () => {
    if (!id) {
      setError('Team ID is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await teamsApi.fetchTeamById(id);
      setTeamData(data);
      // Initialize edit form with current team data
      if (data.team) {
        setEditForm({
          custom_team_name: data.team.custom_team_name || '',
          member_routing_rule: data.team.member_routing_rule || 1,
          object_type: data.team.object_type || 'contact',
          is_default: Boolean(data.team.is_default),
          team_order: data.team.team_order || 1,
          team_active: Boolean(data.team.team_active),
          team_weight: data.team.team_weight ?? null,
          fallback_behavior: data.team.fallback_behavior || 'skip',
          fallback_owner_id: data.team.fallback_owner_id || '',
          owner_property_name: data.team.owner_property_name || '',
          availability_source: data.team.availability_source || 'distributely',
          mock: Boolean(data.team.mock),
          live: Boolean(data.team.live)
        });
      }
    } catch (err) {
      console.error('Error fetching team data:', err);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) fetchTeamData();
  }, [id, fetchTeamData]);

  useEffect(() => {
    if (!teamData?.team?.hs_portal_id) {
      setOwners([]);
      return;
    }

    let isMounted = true;

    const loadOwners = async () => {
      try {
        const data = await membersApi.fetchHubspotOwners(teamData.team.hs_portal_id);
        if (!isMounted) return;
        const ownerList = data?.owners || data?.data || data || [];
        ownerList.sort((a, b) => {
          const aLabel = (a.firstName || a.first_name || '').toLowerCase();
          const bLabel = (b.firstName || b.first_name || '').toLowerCase();
          return aLabel.localeCompare(bLabel);
        });
        setOwners(ownerList);
      } catch (err) {
        console.error('Failed to load owners:', err);
        if (isMounted) {
          setOwners([]);
        }
      }
    };

    loadOwners();

    return () => {
      isMounted = false;
    };
  }, [teamData?.team?.hs_portal_id]);

  useEffect(() => {
    if (!isEditing) return;
    const portalId = teamData?.team?.hs_portal_id;
    if (!portalId) {
      setCustomObjectOptions([]);
      return;
    }

    let isMounted = true;

    const loadCustomObjects = async () => {
      try {
        const response = await customObjectsApi.list(portalId);
        if (!isMounted) return;

        const options = (response?.customObjects || [])
          .filter((item) => Number(item.is_active) === 1)
          .map((item) => ({
            value: item.object_type_key,
            label: item.display_name || item.object_type_key,
          }));

        setCustomObjectOptions(options);
      } catch (err) {
        console.error('Failed to load custom object options:', err);
        if (isMounted) {
          setCustomObjectOptions([]);
        }
      }
    };

    loadCustomObjects();

    return () => {
      isMounted = false;
    };
  }, [isEditing, teamData?.team?.hs_portal_id]);

  useEffect(() => {
    if (!isEditing) return;
    if (!teamData?.team?.hs_portal_id || !editForm.object_type) {
      setProperties([]);
      return;
    }

    let isMounted = true;
    setLoadingProperties(true);

    const loadProperties = async () => {
      try {
        const response = await fieldOptionsApi.fetchProperties(
          editForm.object_type,
          teamData.team.hs_portal_id
        );
        if (!isMounted) return;
        const sortedProps = (response.properties || []).sort((a, b) => {
          const aLabel = (a.label || a.name || '').toLowerCase();
          const bLabel = (b.label || b.name || '').toLowerCase();
          return aLabel.localeCompare(bLabel, 'en', { sensitivity: 'base' });
        });
        setProperties(sortedProps);
      } catch (err) {
        console.error('Failed to load properties:', err);
        if (isMounted) {
          setProperties([]);
        }
      } finally {
        if (isMounted) {
          setLoadingProperties(false);
        }
      }
    };

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, [isEditing, editForm.object_type, teamData?.team?.hs_portal_id]);

  const handleBack = () => navigate('/teams');
  const handleEditMember = (memberId) => navigate(`/memberdetail/${memberId}`);
  const handleConfigureRules = () => navigate(`/teams/${id}/rules`);
  const handleManageMembers = () => navigate(`/teams/${id}/manage-members`);

  const handleExportMembers = async () => {
    try {
      const response = await teamsApi.exportTeamMembers(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `team-${id}-members.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export team members:', err);
      setError('Failed to export team members. Please try again.');
    }
  };

  const handleImportMembers = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setImportingMembers(true);
    setImportError(null);
    setImportResult(null);

    try {
      const csvText = await file.text();
      const result = await teamsApi.importTeamMembers(id, csvText);
      setImportResult(result);
      await fetchTeamData();
    } catch (err) {
      console.error('Failed to import team members:', err);
      setImportError('Failed to import team members. Please check the CSV and try again.');
    } finally {
      setImportingMembers(false);
      event.target.value = '';
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing && teamData?.team) {
      // Reset form when starting edit
      setEditForm({
        custom_team_name: teamData.team.custom_team_name || '',
        member_routing_rule: teamData.team.member_routing_rule || 1,
        object_type: teamData.team.object_type || 'contact',
        is_default: Boolean(teamData.team.is_default),
        team_order: teamData.team.team_order || 1,
        team_active: Boolean(teamData.team.team_active),
        team_weight: teamData.team.team_weight ?? null,
        fallback_behavior: teamData.team.fallback_behavior || 'skip',
        fallback_owner_id: teamData.team.fallback_owner_id || '',
        owner_property_name: teamData.team.owner_property_name || '',
        availability_source: teamData.team.availability_source || 'distributely',
        mock: Boolean(teamData.team.mock),
        live: Boolean(teamData.team.live)
      });
    }
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      if (editForm.fallback_behavior === 'fallback_owner' && !editForm.fallback_owner_id) {
        setError('Select a fallback owner or change the fallback behavior.');
        return;
      }
      await teamsApi.updateTeam(id, editForm);
      setIsEditing(false);
      await fetchTeamData(); // Refresh data
      setError(null); 
    } catch (err) {
      console.error('Error updating team:', err);
      setError('Failed to update team. Please try again.');
    } finally {
      await fetchTeamData();
    }

    
  };

  const team = teamData?.team;
  const members = teamData?.members || [];
  const hasMembers = members.length > 0;
  const objectTypeOptions = useMemo(() => {
    const merged = [...STANDARD_OBJECT_TYPE_OPTIONS];
    const seen = new Set(merged.map((item) => item.value));

    customObjectOptions.forEach((item) => {
      if (!item?.value || seen.has(item.value)) return;
      merged.push(item);
      seen.add(item.value);
    });

    if (editForm.object_type && !seen.has(editForm.object_type)) {
      merged.push({ value: editForm.object_type, label: `Current: ${editForm.object_type}` });
    }

    return merged;
  }, [customObjectOptions, editForm.object_type]);

  const ownerNameById = useMemo(() => {
    const map = new Map();
    owners.forEach((owner) => {
      const ownerId = owner.id || owner.hs_owner_id;
      if (!ownerId) return;
      const firstName = owner.firstName || owner.first_name || '';
      const lastName = owner.lastName || owner.last_name || '';
      const label = `${firstName} ${lastName}`.trim() || owner.email || owner.name || ownerId;
      map.set(String(ownerId), label);
    });
    return map;
  }, [owners]);

  // Sorting logic
  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else setSortField(field);
    setCurrentPage(1);
  };

  const sortMembers = (members) => {
    return [...members].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (sortField === 'date_created' || sortField === 'last_updated') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      return aValue < bValue ? (sortDirection === 'asc' ? -1 : 1) : aValue > bValue ? (sortDirection === 'asc' ? 1 : -1) : 0;
    });
  };

  const paginateMembers = (members) => members.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const displayedMembers = sortMembers(members);
  const paginatedMembers = paginateMembers(displayedMembers);
  const totalPages = Math.ceil(displayedMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  useEffect(() => {
    if (totalPages === 0) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const SortableHeader = ({ field, children }) => (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-200" onClick={() => handleSort(field)}>
      <div className="flex items-center justify-between">
        <span>{children}</span>
        <div className="flex flex-col ml-1">
          <FiChevronUp className={`w-3 h-3 ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`} />
          <FiChevronDown className={`w-3 h-3 -mt-1 ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
        </div>
      </div>
    </th>
  );

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div className="flex items-center">
          <button onClick={handleBack} className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition duration-200" title="Back to Teams">
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{team?.custom_team_name || 'Team Details'}</h1>
            <p className="text-gray-600">{team ? `${team.object_type} Team • Team ID: ${id}` : `Team ID: ${id}`}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex gap-3">
            {/* Manage Team Members Button */}
            <button
              onClick={handleManageMembers}
              className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <FiUsers className="w-4 h-4 mr-2" /> Manage Team Members
            </button>
            {/* Configure Rules Button */}
            <button
              onClick={handleConfigureRules}
              className="flex items-center bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              <FiSettings className="w-4 h-4 mr-2" /> Configure Rules
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExportMembers}
              className="flex items-center bg-white border border-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg hover:border-blue-300 hover:text-blue-600 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Export Members
            </button>
            <label className="flex items-center bg-white border border-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg hover:border-blue-300 hover:text-blue-600 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer">
              {importingMembers ? 'Importing...' : 'Import Members'}
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleImportMembers}
                disabled={importingMembers}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500">
            CSV import supports `add`, `update`, and `delete` in the `action` column. Use `email` to identify owners for add/delete rows (team is inferred from this page).
          </p>
        </div>
      </div>
      {importResult && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          Import complete: {importResult.created || 0} added, {importResult.updated || 0} updated, {importResult.deleted || 0} deleted, {importResult.failed || 0} failed.
        </div>
      )}
      {importResult?.errors?.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          <p className="font-semibold mb-2">Import errors</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="text-xs uppercase tracking-wide text-yellow-700 border-b border-yellow-200">
                <tr>
                  <th className="px-3 py-2">Row</th>
                  <th className="px-3 py-2">Issue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-200">
                {importResult.errors.map((err) => (
                  <tr key={`${err.row}-${err.message}`}>
                    <td className="px-3 py-2">{err.row}</td>
                    <td className="px-3 py-2">{err.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {importError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          {importError}
        </div>
      )}

      {/* Team Edit Form */}
      {isEditing ? (
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Edit Team Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Team Name
                </label>
                <input
                  type="text"
                  value={editForm.custom_team_name}
                  onChange={(e) => handleFormChange('custom_team_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter team name"
                />
              </div>
              
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Object Type
                </label>
                <select
                  value={editForm.object_type}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    handleFormChange('object_type', nextType);
                    handleFormChange('owner_property_name', '');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {objectTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Owner Property
                </label>
                <select
                  value={editForm.owner_property_name || ''}
                  onChange={(e) => handleFormChange('owner_property_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Default (hubspot_owner_id)</option>
                  {editForm.owner_property_name &&
                    !properties.some((property) => property.name === editForm.owner_property_name) && (
                      <option value={editForm.owner_property_name}>
                        Current: {editForm.owner_property_name}
                      </option>
                    )}
                  {properties.map((property) => (
                    <option key={property.name} value={property.name}>
                      {property.label || property.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {loadingProperties
                    ? 'Loading properties…'
                    : 'Applies to this team’s object type.'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Order
                </label>
                <input
                  type="number"
                  value={editForm.team_order}
                  onChange={(e) => handleFormChange('team_order', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Weight
                </label>
                <input
                  type="number"
                  value={editForm.team_weight ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFormChange('team_weight', value === '' ? null : parseInt(value));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="0"
                  placeholder="No weight assigned"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no weight assignment</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fallback Behavior
                </label>
                <select
                  value={editForm.fallback_behavior}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    handleFormChange('fallback_behavior', nextValue);
                    if (nextValue !== 'fallback_owner') {
                      handleFormChange('fallback_owner_id', '');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="skip">Skip to next team</option>
                  <option value="fallback_owner">Fallback owner</option>
                  <option value="standard_rr">Standard round robin</option>
                  <option value="ignore_availability_rr">Ignore availability (round robin)</option>
                  <option value={"ignore_schedule_availability_rr"}>Ignore availability schedules only (round robin)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fallback Owner
                </label>
                <select
                  value={editForm.fallback_owner_id}
                  onChange={(e) => handleFormChange('fallback_owner_id', e.target.value)}
                  disabled={editForm.fallback_behavior !== 'fallback_owner'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">No fallback owner</option>
                  {owners.map((owner) => {
                    const ownerId = owner.id || owner.hs_owner_id;
                    const label = ownerNameById.get(String(ownerId)) || ownerId;
                    return (
                      <option key={ownerId} value={ownerId}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                {editForm.fallback_behavior !== 'fallback_owner' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select “Fallback owner” to enable this option.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability Source
                </label>
                <select
                  value={editForm.availability_source}
                  onChange={(e) => handleFormChange('availability_source', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="distributely">Distributely schedules</option>
                  <option value="hubspot">HubSpot user schedules (runtime)</option>
                  <option value={"talkdesk"}>TalkDesk Availability</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  HubSpot mode uses Users API working hours and out-of-office data.
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.is_default}
                    onChange={(e) => handleFormChange('is_default', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Is Default Team</span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.team_active}
                    onChange={(e) => handleFormChange('team_active', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Team Active</span>
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.mock}
                    onChange={(e) => handleFormChange('mock', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Mock</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.live}
                    onChange={(e) => handleFormChange('live', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Live</span>
                </label>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleEditToggle}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition duration-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Team Information</h2>
            <button
              onClick={handleEditToggle}
              className="flex items-center px-3 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition duration-200"
            >
              <FiEdit className="w-4 h-4 mr-1" />
              Edit
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500">Object Type</dt>
                <dd className="mt-1 text-sm text-gray-900 capitalize">{team?.object_type || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Owner Property</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {team?.owner_property_name || 'hubspot_owner_id'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Team Order</dt>
                <dd className="mt-1 text-sm text-gray-900">{team?.team_order || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Team Weight</dt>
                <dd className="mt-1 text-sm text-gray-900">{team?.team_weight ?? 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fallback Behavior</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {team?.fallback_behavior === 'fallback_owner'
                    ? 'Fallback owner'
                    : team?.fallback_behavior === 'standard_rr'
                      ? 'Standard round robin'
                    : team?.fallback_behavior === 'ignore_availability_rr'
                      ? 'Ignore availability (round robin)'
                              : team?.fallback_behavior === 'ignore_schedule_availability_rr'
                      ? 'Ignore availability schedules only (round robin)'
                      : 'Skip to next team'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Fallback Owner</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {team?.fallback_owner_id
                    ? (ownerNameById.get(String(team.fallback_owner_id)) || team.fallback_owner_id)
                    : 'None'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Availability Source</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {team?.availability_source === 'hubspot'
                    ? 'HubSpot user schedules (runtime)'
                    : team?.availability_source === 'talkdesk'
                    ? 'TalkDesk Availability'
                    : 'Distributely schedules'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Default Team</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${team?.is_default ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {team?.is_default ? 'Yes' : 'No'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Team Active</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${team?.team_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {team?.team_active ? 'Active' : 'Inactive'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Mock</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${team?.mock ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {team?.mock ? 'Enabled' : 'Disabled'}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Live</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${team?.live ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {team?.live ? 'Enabled' : 'Disabled'}
                  </span>
                </dd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Members */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mx-8 mb-4">
              <p className="text-red-800">{error}</p>
              <button onClick={fetchTeamData} className="mt-2 text-red-600 hover:text-red-700 text-sm underline">Retry</button>
            </div>
          </div>
        ) : hasMembers ? (
          <>
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
              <p className="text-sm text-gray-600 mt-1">
                Showing {paginatedMembers.length} of {displayedMembers.length} member{displayedMembers.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortableHeader field="last_name">Last Name</SortableHeader>
                    <SortableHeader field="first_name">First Name</SortableHeader>
                    <SortableHeader field="weight">Weight</SortableHeader>
                    <SortableHeader field="date_created">Date Created</SortableHeader>
                    <SortableHeader field="last_updated">Last Updated</SortableHeader>
                    <SortableHeader field="member_active">Active</SortableHeader>
                    <SortableHeader field="hs_owner_id">HubSpot Owner ID</SortableHeader>
                    <SortableHeader field="hs_user_id">HubSpot User ID</SortableHeader>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedMembers.map(member => (
                    <tr key={member.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 cursor-pointer" onClick={() => handleEditMember(member.id)}>{member.last_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.first_name || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {editingWeightMemberId === member.id ? (
                          <input
                            type="number"
                            min="1"
                            value={weightDraft}
                            onChange={(e) => setWeightDraft(e.target.value)}
                            onBlur={() => saveMemberWeight(member.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveMemberWeight(member.id);
                              if (e.key === 'Escape') {
                                setEditingWeightMemberId(null);
                                setWeightDraft('');
                              }
                            }}
                            className="w-20 px-2 py-1 border rounded"
                            autoFocus
                            disabled={savingWeightMemberId === member.id}
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setEditingWeightMemberId(member.id);
                              setWeightDraft(member.weight ?? '');
                            }}
                            className="text-blue-600 hover:text-blue-800 underline"
                            disabled={savingWeightMemberId === member.id}
                          >
                            {savingWeightMemberId === member.id ? "Saving..." : (member.weight ?? 'N/A')}
                          </button>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(member.date_created)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(member.last_updated)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => toggleMemberActive(member.id, member.member_active)}
                          disabled={savingActiveMemberId === member.id}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full transition duration-200 ${
                            member.member_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } disabled:cursor-not-allowed disabled:opacity-60`}
                          aria-label={`Set ${member.first_name || ''} ${member.last_name || ''} ${member.member_active ? 'inactive' : 'active'}`.trim()}
                        >
                          {savingActiveMemberId === member.id
                            ? 'Saving...'
                            : member.member_active
                              ? 'Yes'
                              : 'No'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.hs_owner_id || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.hs_user_id || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap"><button onClick={() => handleEditMember(member.id)} className="text-blue-600 hover:text-blue-800"><FiEdit className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, displayedMembers.length)} of {displayedMembers.length} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 rounded-lg text-sm ${
                            currentPage === pageNumber
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No team members</h3>
            <p className="text-gray-600 mb-6">This team doesn't have any members yet.</p>
          </div>
        )}
      </div>
    </div>
  );
}
